export enum BillingType {
  Standard = 'standard',
  Interval = 'interval',
  NoContract = 'null'
}

export interface ContractRevision {
  contractRevisionId: number
  amount: number
  startByDate: string
  dueDate: string
  menuItemsSnapshot: MenuItemsSnapshot[]
  availablePaymentPlansSnapshot: AvailablePaymentPlansSnapshot[]
  selectedPaymentPlanSnapshot: SelectedPaymentPlanSnapshot | null
  availableIntervalBillingSchedulesSnapshot: AvailableIntervalBillingSchedulesSnapshot[]
  selectedIntervalBillingScheduleSnapshot: SelectedIntervalBillingScheduleSnapshot | null
  paymentPlanSelectedBy: null
  status: number
  requireAllSignatures: boolean
  isSticky: boolean
  districtUserId: number | null
  officeApprovalMemo: null | string
  createdDate: Date
  active: boolean
  deleted: boolean
  contract: Contract
  /** DTA-1092: the contract's module, else its programs' (1012 = childcare). */
  effectiveModuleId?: number | null
  /** DTA-1092: shown in the Childcare app, not on Billing (childcare, and the district uses the app). */
  inChildcareApp?: boolean
  invoiceHeaders: InvoiceHeader[]
  familyUserAssociations: FamilyUserAssociation[]
  previousRevisionIds: any[]
  paymentSchedule: PaymentSchedule[]
  eligibleForPayment: boolean
  eligibleForAutoPayment: boolean
  autoPayEnabled: boolean
  autoPaymentFundingSource: number
  requiresAutoPay: boolean
  menuItemsSnapshotGroupedBy: MenuItemsSnapshotGroupedByElement[]
  studentAccountId?: number
  // Only returned for signed revisions; the unsigned endpoint omits them.
  invoiceHeadersTotalAmount?: number
  invoiceHeadersTotalPaid?: number
}

export interface AvailableIntervalBillingSchedulesSnapshot {
  scheduleId: number
  name: string
  districtId: number
  type: string
  periods: AvailableIntervalBillingSchedulesSnapshotPeriods
  discountCode: string
  billPastDue: boolean
}

export interface AvailableIntervalBillingSchedulesSnapshotPeriods {
  version: string
  list: {
    name: string
    billingDate: Date
    dueDate: Date
    invoiceHeaderId: null
    menuItems: MenuItem[]
    billingPeriodStart: string
    billingPeriodEnd: string
  }[]
}

export interface MenuItem {
  menuItemId: number | string
  price: number | string
  quantity: number | string
  description: string
}

export interface AvailablePaymentPlansSnapshot {
  paymentPlanId: number
  districtId: number
  name: string
  type: number
  frequency: number | null
  minPaymentDay: number | null
  maxPaymentDay: number | null
  customSchedule: null
  customScheduleIsFlat: boolean
  allowPartial: boolean
  requiresAutoPay: boolean
  discount: null
  discountIsFlat: boolean
  minimumAmount: number | null
  maximumAmount: null
  discountCode: null
  targetAmount: null
  lateFirstPayment: boolean
  firstPaymentDate: null
  lastPaymentDate: null
}

export interface Contract {
  contractId: number
  districtId: number
  name: string
  programYear: ProgramYear
  moduleId: number
  billingType: string
  createdDate: Date
  updatedDate: Date
  depositAmount: number
  depositIsCollected: boolean
  customSignedMessage: string
  customOverviewMessage: string
  autoPaymentFundingSourceId: number
  autoPaymentEnabled: boolean
  autoPayEnabledUserId: number | null
  active: boolean
  deleted: boolean
  unpaidFamilyUserLicenses: FamilyUserLicense[]
  invoiceHeaders: InvoiceHeader[]
  depositCreditAccount: DepositCreditAccount
  students: TStudent[]
}

export interface ProgramYear {
  programYearId: number
  description: string
  active: boolean
}

export interface FamilyUserLicense {
  contractRevisionLicenseId: number
  payerType: string
  amount: number
  paid: boolean
  deleted: boolean
}

export interface DepositCreditAccount {
  creditAccountId: number
  name: string
  districtId: number
  associationType: string
  active: boolean
  balance: number
}

export interface InvoiceHeader {
  invoiceHeaderId: number
  districtId: number
  orderItemTypeId: number
  billToText: string
  billToTextLocked: boolean
  shipToText: null
  totalAmount: number
  dueDate: Date
  status: number
  programYearId: number
  invoiceItemLog: InvoiceItemLog | null
  createdDate: Date
  deleted: boolean
  amountRemaining?: number
  description?: string
}

export interface InvoiceItemLog {
  updateTime: Date
  districtUserId: string
  oldInvoiceItems: The1[]
  newInvoiceItems: NewInvoiceItems
}

export interface NewInvoiceItems {
  '1': The1
}

export interface The1 {
  invoiceItemId: number
  studentAccountId: number
  menuItemId: number
  quantity: number
  epaytrakFeeId: null
  description: string
  price: number
  createdDate: Date
  locked: boolean
  deleted: boolean
  earned: boolean
  earnDate: null
}

export interface FamilyUserAssociation {
  assocId: number
  familyUserId: null | number
  formsSnapshot: FormsSnapshot[]
  formsUpdatedDate: Date | null
  formSubmissionId: null | string
  signature: null | string
  signedDate: Date | null
  createdDate: Date
  updatedDate: Date | null
  deleted: boolean
}

export interface FormsSnapshot {
  formId: number
  name: string
  questions: Question[]
}

export interface Question {
  questionId: number
  label: string
  type: number
  options: null
  validationRules: string[]
  userResponse: boolean
  displayOptions?: null
}

export interface MenuItemsSnapshot {
  invoiceHeaderId?: number
  invoiceItemId?: number
  menuItemId: number
  description: string
  price: number
  originalPrice: number
  quantity: number
  studentAccountId: number
  contractAssocId: number
  extraData: null
  childcareTransactions?: any[]
}

export interface MenuItemsSnapshotGroupedByElement {
  studentAccountId?: number
  menuItemId: number
  quantity: number
  description: string
  price: number
}

export interface PaymentSchedule {
  contractRevisionId: number
  scheduledPaymentId: number
  description: string
  amountPaid: number
  amountDue: number
  dueDate: Date
  billingDate: Date | undefined
  type: number
  invoiceHeaderId?: number
  intervalBillingDescription?: string
  allowPartial?: boolean
  firstPaymentIndex?: null
  uniqueId?: string
  feeCategory?: string
  feeId?: number
  contractId?: number
  eligibleForAutoPayment?: boolean
  revisionPaymentType?: number
  amount?: number
  contractName?: string
  familyAccountId?: number
}

export interface SelectedIntervalBillingScheduleSnapshot {
  scheduleId: number
  name: string
  districtId: number
  type: string
  periods: SelectedIntervalBillingScheduleSnapshotPeriods
  discountCode: string
  billPastDue: boolean
}

export interface SelectedIntervalBillingScheduleSnapshotPeriods {
  version: string
  list: FluffyList[]
}

export interface FluffyList {
  name: string
  billingDate: Date
  dueDate: Date
  invoiceHeaderId: number
  menuItems: MenuItemsSnapshotGroupedByElement[]
  billingPeriodStart: string
  billingPeriodEnd: string
}

export interface SelectedPaymentPlanSnapshot {
  paymentPlanId: number
  type: number
  frequency: number
  paymentDay: number
  customScheduleIsFlat: boolean
  startDate: Date
  allowPartial: boolean
  requiresAutoPay: boolean
  targetAmount: null
  lateFirstPayment: boolean
  firstPaymentDate: string
  lastPaymentDate: string
  customSchedule: any[]
  minPaymentDay: number
  maxPaymentDay: number
  discountCode: null
}

export interface ProcessingFeeConfig {
  version: number
  isFlat: boolean
  thresholdStep: number
  amountStart: number
  amountStep: number
}
